import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Play, Pause, RotateCcw, FileVideo, Check, X, Download } from 'lucide-react';
import { PoseLandmarker, FilesetResolver, DrawingUtils } from '@mediapipe/tasks-vision';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Right leg landmarks
const LANDMARKS = {
  RIGHT_HIP: 24,
  RIGHT_KNEE: 26,
  RIGHT_ANKLE: 28,
  RIGHT_HEEL: 30,
  RIGHT_FOOT_INDEX: 32
};

const CONFIG = {
  VIS_MIN: 0.50,
  UP_DEG: 6.0,
  DOWN_DEG: 2.0,
  KNEE_MIN_DEG: 155.0,
  RISE_NORM_MIN: 0.015,
  TOP_HOLD_MS: 150,
  MIN_REP_GAP_MS: 500,
  EMA_ALPHA: 0.08,
  CALIBRATION_FRAMES: 20,
  BUFFER_SIZE: 5
};

// Helper functions
const ema = (prev, x, alpha) => prev === null ? x : (1 - alpha) * prev + alpha * x;

const angle3pt = (a, b, c) => {
  const ba = [a[0] - b[0], a[1] - b[1]];
  const bc = [c[0] - b[0], c[1] - b[1]];
  const nba = Math.sqrt(ba[0] * ba[0] + ba[1] * ba[1]);
  const nbc = Math.sqrt(bc[0] * bc[0] + bc[1] * bc[1]);
  if (nba < 1e-6 || nbc < 1e-6) return NaN;
  let cosang = (ba[0] * bc[0] + ba[1] * bc[1]) / (nba * nbc);
  cosang = Math.max(-1.0, Math.min(1.0, cosang));
  return Math.acos(cosang) * (180 / Math.PI);
};

const getLandmark = (landmarks, idx) => {
  const lm = landmarks[idx];
  if (!lm) return null;
  return { xy: [lm.x, lm.y], visibility: lm.visibility || 0 };
};

const movingAverage = (buffer) => buffer.length === 0 ? 0 : buffer.reduce((a, b) => a + b, 0) / buffer.length;

const VideoAnalysisPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const poseLandmarkerRef = useRef(null);
  const drawingUtilsRef = useRef(null);
  const analysisRef = useRef(null);

  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFeedback, setCurrentFeedback] = useState('');

  // Analysis results
  const [results, setResults] = useState({
    totalReps: 0,
    repTimestamps: [],
    avgKneeAngle: 0,
    avgFootAngle: 0,
    formIssues: [],
    duration: 0,
    calibrationTime: 0,
    framesAnalyzed: 0,
    detectionRate: 0
  });

  // Initialize MediaPipe
  useEffect(() => {
    const init = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
        );
        poseLandmarkerRef.current = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numPoses: 1
        });
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to init MediaPipe:', err);
        setIsLoading(false);
      }
    };
    init();

    return () => {
      if (poseLandmarkerRef.current) {
        poseLandmarkerRef.current.close();
      }
    };
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      setVideoUrl(URL.createObjectURL(file));
      setAnalysisComplete(false);
      setResults({
        totalReps: 0, repTimestamps: [], avgKneeAngle: 0, avgFootAngle: 0,
        formIssues: [], duration: 0, calibrationTime: 0, framesAnalyzed: 0, detectionRate: 0
      });
    }
  };

  // Analyze video
  const analyzeVideo = useCallback(async () => {
    if (!videoRef.current || !poseLandmarkerRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!drawingUtilsRef.current) {
      drawingUtilsRef.current = new DrawingUtils(ctx);
    }

    // Reset analysis state
    analysisRef.current = {
      calibrated: false,
      calibrationFrames: [],
      baselineFootAngle: null,
      baselineHeelY: null,
      baseShinLen: null,
      inRaise: false,
      repCount: 0,
      repTimestamps: [],
      topEnteredTime: null,
      lastRepTime: 0,
      smoothFootAngle: null,
      smoothKneeAngle: null,
      smoothRiseNorm: null,
      footAngleBuffer: [],
      kneeAngleBuffer: [],
      riseBuffer: [],
      kneeAngles: [],
      footAngles: [],
      formIssues: [],
      framesWithDetection: 0,
      totalFrames: 0,
      calibrationTime: 0
    };

    setIsAnalyzing(true);
    setIsPaused(false);
    setProgress(0);
    setCurrentFeedback('Starting analysis...');

    video.currentTime = 0;
    await video.play();

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const processFrame = async () => {
      if (video.paused || video.ended) {
        if (video.ended) {
          finishAnalysis();
        }
        return;
      }

      const state = analysisRef.current;
      state.totalFrames++;

      try {
        const results = poseLandmarkerRef.current.detectForVideo(video, performance.now());

        // Clear and draw video frame
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (results.landmarks && results.landmarks.length > 0) {
          const landmarks = results.landmarks[0];
          state.framesWithDetection++;

          // Get right leg landmarks
          const hip = getLandmark(landmarks, LANDMARKS.RIGHT_HIP);
          const knee = getLandmark(landmarks, LANDMARKS.RIGHT_KNEE);
          const ankle = getLandmark(landmarks, LANDMARKS.RIGHT_ANKLE);
          const heel = getLandmark(landmarks, LANDMARKS.RIGHT_HEEL);
          const toe = getLandmark(landmarks, LANDMARKS.RIGHT_FOOT_INDEX);

          const allVisible = hip && knee && ankle && heel && toe &&
            hip.visibility >= CONFIG.VIS_MIN && knee.visibility >= CONFIG.VIS_MIN &&
            ankle.visibility >= CONFIG.VIS_MIN && heel.visibility >= CONFIG.VIS_MIN &&
            toe.visibility >= CONFIG.VIS_MIN;

          if (allVisible) {
            // Draw skeleton
            drawingUtilsRef.current.drawConnectors(landmarks, PoseLandmarker.POSE_CONNECTIONS, 
              { color: 'rgba(100,100,100,0.4)', lineWidth: 1 });

            // Draw right leg highlighted
            const rightLegColor = state.inRaise ? '#22c55e' : '#f97316';
            const rightLegIndices = [24, 26, 28, 30, 32];
            const rightLegLandmarks = rightLegIndices.map(i => landmarks[i]).filter(Boolean);
            drawingUtilsRef.current.drawLandmarks(rightLegLandmarks, 
              { color: rightLegColor, fillColor: rightLegColor, radius: 8 });

            // Calculate metrics
            const calcFootAngle = (h, t) => Math.atan2(-(t[1] - h[1]), Math.abs(t[0] - h[0])) * (180 / Math.PI);
            const rawFootAngle = calcFootAngle(heel.xy, toe.xy);
            const rawKneeAngle = angle3pt(hip.xy, knee.xy, ankle.xy);
            const shinLen = Math.sqrt(Math.pow(knee.xy[0] - ankle.xy[0], 2) + Math.pow(knee.xy[1] - ankle.xy[1], 2));

            // Calibration phase
            if (!state.calibrated) {
              state.calibrationFrames.push({ footAngle: rawFootAngle, heelY: heel.xy[1], shinLen, kneeAngle: rawKneeAngle });
              setCurrentFeedback(`Calibrating... ${state.calibrationFrames.length}/${CONFIG.CALIBRATION_FRAMES}`);

              if (state.calibrationFrames.length >= CONFIG.CALIBRATION_FRAMES) {
                const frames = state.calibrationFrames;
                const sorted = (arr) => [...arr].sort((a, b) => a - b);
                const mid = Math.floor(frames.length / 2);
                state.baselineFootAngle = sorted(frames.map(f => f.footAngle))[mid];
                state.baselineHeelY = sorted(frames.map(f => f.heelY))[mid];
                state.baseShinLen = sorted(frames.map(f => f.shinLen))[mid];
                state.smoothFootAngle = state.baselineFootAngle;
                state.smoothKneeAngle = 180;
                state.smoothRiseNorm = 0;
                state.calibrated = true;
                state.calibrationTime = video.currentTime;
                setCurrentFeedback('Calibration complete! Analyzing movement...');
              }
            } else {
              // Analysis phase
              const heelRise = state.baselineHeelY - heel.xy[1];
              const rawRiseNorm = heelRise / state.baseShinLen;

              // Smoothing
              state.footAngleBuffer.push(rawFootAngle);
              state.kneeAngleBuffer.push(rawKneeAngle);
              state.riseBuffer.push(rawRiseNorm);
              if (state.footAngleBuffer.length > CONFIG.BUFFER_SIZE) state.footAngleBuffer.shift();
              if (state.kneeAngleBuffer.length > CONFIG.BUFFER_SIZE) state.kneeAngleBuffer.shift();
              if (state.riseBuffer.length > CONFIG.BUFFER_SIZE) state.riseBuffer.shift();

              const maFootAngle = movingAverage(state.footAngleBuffer);
              const maKneeAngle = movingAverage(state.kneeAngleBuffer);
              const maRiseNorm = movingAverage(state.riseBuffer);

              state.smoothFootAngle = ema(state.smoothFootAngle, maFootAngle, CONFIG.EMA_ALPHA);
              state.smoothKneeAngle = ema(state.smoothKneeAngle, maKneeAngle, CONFIG.EMA_ALPHA);
              state.smoothRiseNorm = ema(state.smoothRiseNorm, maRiseNorm, CONFIG.EMA_ALPHA);

              const footAngleDelta = state.smoothFootAngle - state.baselineFootAngle;

              // Track for averages
              state.kneeAngles.push(state.smoothKneeAngle);
              state.footAngles.push(footAngleDelta);

              // Check conditions
              const footUp = footAngleDelta >= CONFIG.UP_DEG;
              const footDown = footAngleDelta < CONFIG.DOWN_DEG;
              const kneeOk = state.smoothKneeAngle >= CONFIG.KNEE_MIN_DEG;
              const riseOk = state.smoothRiseNorm >= CONFIG.RISE_NORM_MIN;

              const now = video.currentTime * 1000;

              // State machine
              if (!state.inRaise) {
                if (footUp && riseOk && kneeOk) {
                  state.inRaise = true;
                  state.topEnteredTime = now;
                  setCurrentFeedback(`Rep in progress... (${state.repCount} completed)`);
                } else if (!kneeOk && footAngleDelta > 2) {
                  // Form issue: knee bent during raise attempt
                  state.formIssues.push({ time: video.currentTime, issue: 'Knee bent during raise', kneeAngle: state.smoothKneeAngle });
                }
              } else {
                if (footDown) {
                  const holdTime = now - state.topEnteredTime;
                  const timeSinceLastRep = now - state.lastRepTime;

                  if (holdTime >= CONFIG.TOP_HOLD_MS && timeSinceLastRep >= CONFIG.MIN_REP_GAP_MS) {
                    state.repCount++;
                    state.repTimestamps.push({ rep: state.repCount, time: video.currentTime, kneeAngle: state.smoothKneeAngle, footAngle: footAngleDelta });
                    state.lastRepTime = now;
                    setCurrentFeedback(`Rep ${state.repCount} counted at ${video.currentTime.toFixed(1)}s`);
                  }
                  state.inRaise = false;
                  state.topEnteredTime = null;
                }
              }
            }
          } else {
            setCurrentFeedback('Right leg not fully visible in frame');
          }
        }

        // Update progress
        setProgress((video.currentTime / video.duration) * 100);

      } catch (err) {
        console.error('Frame error:', err);
      }

      requestAnimationFrame(processFrame);
    };

    processFrame();
  }, []);

  // Finish analysis and compile results
  const finishAnalysis = useCallback(async () => {
    const state = analysisRef.current;
    const video = videoRef.current;

    const avgKnee = state.kneeAngles.length > 0 
      ? state.kneeAngles.reduce((a, b) => a + b, 0) / state.kneeAngles.length 
      : 0;
    const avgFoot = state.footAngles.length > 0 
      ? state.footAngles.reduce((a, b) => a + b, 0) / state.footAngles.length 
      : 0;

    const finalResults = {
      totalReps: state.repCount,
      repTimestamps: state.repTimestamps,
      avgKneeAngle: Math.round(avgKnee),
      avgFootAngle: Math.round(avgFoot * 10) / 10,
      formIssues: state.formIssues.slice(0, 10), // Limit to 10
      duration: Math.round(video.duration),
      calibrationTime: Math.round(state.calibrationTime * 10) / 10,
      framesAnalyzed: state.totalFrames,
      detectionRate: Math.round((state.framesWithDetection / state.totalFrames) * 100)
    };

    setResults(finalResults);
    setIsAnalyzing(false);
    setAnalysisComplete(true);
    setCurrentFeedback('Analysis complete!');

    // Save to database
    try {
      await axios.post(`${API}/sessions`, {
        exercise_type: 'right_leg_calf_raise_video',
        reps_completed: finalResults.totalReps,
        goal: finalResults.totalReps,
        duration_seconds: finalResults.duration
      });
    } catch (err) {
      console.error('Failed to save:', err);
    }
  }, []);

  // Pause/Resume
  const togglePause = () => {
    if (videoRef.current) {
      if (isPaused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  // Reset
  const resetAnalysis = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsAnalyzing(false);
    setAnalysisComplete(false);
    setProgress(0);
    setCurrentFeedback('');
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="fixed inset-0 carbon-texture pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 p-6 flex items-center gap-4 border-b border-zinc-800">
        <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-gradient">VIDEO ANALYSIS</h1>
          <p className="text-zinc-500 text-sm">Upload a video for calf raise assessment</p>
        </div>
      </header>

      <main className="relative z-10 p-6 max-w-6xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading AI model...</p>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Video Section */}
            <div className="space-y-4">
              {/* Upload area */}
              {!videoUrl && (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="glass rounded-2xl p-12 border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 cursor-pointer transition-colors"
                >
                  <div className="text-center">
                    <Upload className="mx-auto mb-4 text-zinc-500" size={48} />
                    <p className="text-lg text-white mb-2">Upload Video</p>
                    <p className="text-sm text-zinc-500">MP4, WebM, MOV supported</p>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Video player */}
              {videoUrl && (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden bg-black">
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      className="w-full"
                      playsInline
                      muted
                    />
                    <canvas
                      ref={canvasRef}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                    />

                    {/* Progress overlay */}
                    {isAnalyzing && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
                        <div className="h-full bg-cyan-500 transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    )}
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-3">
                    {!isAnalyzing && !analysisComplete && (
                      <button
                        onClick={analyzeVideo}
                        className="flex-1 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full flex items-center justify-center gap-2"
                      >
                        <Play size={20} fill="currentColor" />
                        Analyze Video
                      </button>
                    )}

                    {isAnalyzing && (
                      <>
                        <button
                          onClick={togglePause}
                          className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-full"
                        >
                          {isPaused ? <Play size={20} /> : <Pause size={20} />}
                        </button>
                        <div className="flex-1 glass rounded-full px-4 py-2">
                          <p className="text-sm text-cyan-400 truncate">{currentFeedback}</p>
                        </div>
                      </>
                    )}

                    {analysisComplete && (
                      <button
                        onClick={resetAnalysis}
                        className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-full"
                      >
                        <RotateCcw size={20} />
                      </button>
                    )}

                    <button
                      onClick={() => { resetAnalysis(); setVideoUrl(null); setVideoFile(null); }}
                      className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-full"
                    >
                      <FileVideo size={20} />
                    </button>
                  </div>

                  {/* File info */}
                  {videoFile && (
                    <p className="text-xs text-zinc-500">
                      {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(1)} MB)
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Results Section */}
            <div className="space-y-4">
              <div className="glass rounded-2xl p-6">
                <h2 className="font-heading text-xl font-bold text-white mb-4">ANALYSIS REPORT</h2>

                {!analysisComplete ? (
                  <div className="text-center py-8">
                    <FileVideo className="mx-auto mb-4 text-zinc-600" size={48} />
                    <p className="text-zinc-500">
                      {videoUrl ? 'Click "Analyze Video" to start' : 'Upload a video to begin analysis'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                        <p className="text-4xl font-bold text-cyan-400 font-data">{results.totalReps}</p>
                        <p className="text-xs text-zinc-500 uppercase">Total Reps</p>
                      </div>
                      <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                        <p className="text-4xl font-bold text-white font-data">{results.duration}s</p>
                        <p className="text-xs text-zinc-500 uppercase">Duration</p>
                      </div>
                      <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                        <p className={`text-2xl font-bold font-data ${results.avgKneeAngle >= 155 ? 'text-green-400' : 'text-orange-400'}`}>
                          {results.avgKneeAngle}°
                        </p>
                        <p className="text-xs text-zinc-500 uppercase">Avg Knee Angle</p>
                      </div>
                      <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-white font-data">{results.detectionRate}%</p>
                        <p className="text-xs text-zinc-500 uppercase">Detection Rate</p>
                      </div>
                    </div>

                    {/* Rep Timeline */}
                    {results.repTimestamps.length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-zinc-400 uppercase mb-2">Rep Timeline</h3>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {results.repTimestamps.map((rep, i) => (
                            <div key={i} className="flex items-center justify-between bg-zinc-900/30 rounded-lg px-3 py-2">
                              <span className="text-cyan-400 font-bold">Rep {rep.rep}</span>
                              <span className="text-zinc-400 text-sm">{rep.time.toFixed(1)}s</span>
                              <span className={`text-sm ${rep.kneeAngle >= 155 ? 'text-green-400' : 'text-orange-400'}`}>
                                {Math.round(rep.kneeAngle)}° knee
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Form Issues */}
                    {results.formIssues.length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-zinc-400 uppercase mb-2">Form Issues Detected</h3>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {results.formIssues.map((issue, i) => (
                            <div key={i} className="flex items-center gap-2 text-orange-400 text-sm">
                              <X size={14} />
                              <span>{issue.issue} at {issue.time.toFixed(1)}s</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Assessment */}
                    <div className="border-t border-zinc-800 pt-4">
                      <h3 className="text-sm font-bold text-zinc-400 uppercase mb-2">Assessment</h3>
                      <div className={`p-4 rounded-xl ${
                        results.totalReps >= 10 && results.avgKneeAngle >= 155 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-orange-500/10 border border-orange-500/30'
                      }`}>
                        {results.totalReps >= 10 && results.avgKneeAngle >= 155 ? (
                          <div className="flex items-center gap-2 text-green-400">
                            <Check size={20} />
                            <span>Good form! {results.totalReps} reps completed with proper technique.</span>
                          </div>
                        ) : (
                          <div className="text-orange-400 space-y-1">
                            {results.totalReps < 10 && <p>• Try to complete at least 10 reps</p>}
                            {results.avgKneeAngle < 155 && <p>• Keep your knee straighter during raises</p>}
                            {results.formIssues.length > 3 && <p>• Focus on maintaining proper form throughout</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default VideoAnalysisPage;
